import React, { useState } from "react";
import styles from "./MainContent.module.scss";
import usersIcon from "@/assets/icons/users-colored.svg";
import videoIcon from "@/assets/icons/film.svg";
import musicIcon from "@/assets/icons/music-content.svg";
import booksIcon from "@/assets/icons/book-content.svg";
import adminsIcon from "@/assets/icons/admins-colored.svg";
import featuredContentIcon from "@/assets/icons/featured-gold.svg";
import AIIcon from "@/assets/icons/ai-colored.svg";
import { Link } from "react-router-dom";
import storyTeller from "@/assets/icons/storyTellerIcon.svg";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { useGetAdminByEmailQuery } from "@/store/rtk-api/admin.api";
import { AdminRole } from "@/store/models/admin.models";
import { useGetSubscribedUsersQuery } from "@/store/rtk-api/users.api";
import { calculateAge } from "@/helpers/calculateAge";
import exportFromJSON from "export-from-json";
import { IUser } from "@/store/models/users.models";
import { notification } from "antd";
import { useUploadMediaFromCsvMutation } from "@/store/rtk-api/media-upload.api";

const cardData = [
  {
    icon: usersIcon,
    label: "Users",
    link: "/users",
  },
  {
    icon: videoIcon,
    label: "Videos",
    link: "/videos",
  },
  {
    icon: musicIcon,
    label: "Music",
    link: "/songs",
  },
  {
    icon: booksIcon,
    label: "Books",
    link: "/books",
  },
  {
    icon: AIIcon,
    label: "AI Prompts",
    link: "/ai-prompts",
  },
  {
    icon: storyTeller,
    label: "StoryTeller",
    link: "/story-teller/hero",
  },
  {
    icon: featuredContentIcon,
    label: "Featured Content",
    link: "/featured-content",
  },
];
const MainContent = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const user = useSelector((state: RootState) => state.user.user);
  const encodedEmail = encodeURIComponent(user?.email as string);
  const { data: adminData } = useGetAdminByEmailQuery(encodedEmail);

  const { data: usersData } = useGetSubscribedUsersQuery({});

  const [uploadContentCsv] = useUploadMediaFromCsvMutation();

  const downloadCSV = (usersData: IUser[]) => {
    if (!usersData) {
      console.error("No user data available.");
      return;
    }

    const filteredUsers = usersData
      .filter((user) => user.isSubscribedToEmail)
      .map((user) => ({
        Email: user.email[0],
        "Sub Accounts": user.profiles.length,
        "Sub Account Names": user.profiles
          .map((profile) => `${profile.name}`)
          .join(", "),
        "Sub Account Ages": user.profiles
          .map((profile) => `${calculateAge(profile.birthday)}`)
          .join(", "),
      }));

    const fileName = "subscribed_users";
    const exportType = exportFromJSON.types.csv;

    exportFromJSON({
      data: filteredUsers,
      fileName,
      fields: [
        "Email",
        "Sub Accounts",
        "Sub Account Names",
        "Sub Account Ages",
      ],
      exportType,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const uploadContent = async () => {
    if (!selectedFile) {
      alert("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("csvFile", selectedFile);

    try {
      await uploadContentCsv(formData).unwrap();
      notification.success({
        message: "Success",
        description: "File uploaded successfully!",
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      notification.error({
        message: "Error",
        description: "Error uploading file",
      });
    }

    setSelectedFile(null);
  };

  return (
    <div className={styles.cardsWrap}>
      {cardData.map((card, index) => (
        <div key={index} className={styles.container}>
          <div className={styles.content}>
            <div className={styles.iconWrap}>
              <img className={styles.icon} src={card.icon} alt={card.label} />
            </div>
            <div className={styles.contentText}>
              <p className={styles.label}>{card.label}</p>
            </div>
          </div>
          <Link to={card.link}>
            <button className={styles.btn}>View</button>
          </Link>
        </div>
      ))}

      {adminData?.status === AdminRole.SUPER_ADMIN && (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.iconWrap}>
              <img className={styles.icon} src={adminsIcon} alt="Admins" />
            </div>
            <div className={styles.contentText}>
              <p className={styles.label}>Admins</p>
            </div>
          </div>
          <Link to="/admins">
            <button className={styles.btn}>View</button>
          </Link>
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.iconWrap}>
            <img className={styles.icon} src={usersIcon} alt="Download Users" />
          </div>
          <div className={styles.contentText}>
            <p className={styles.label}>Download Subscribed Users</p>
          </div>
        </div>
        <button className={styles.btn} onClick={() => downloadCSV(usersData!)}>
          Download CSV
        </button>
      </div>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.iconWrap}>
            <img
              className={styles.icon}
              src={usersIcon}
              alt="Upload content from .csv"
            />
          </div>
          <div className={styles.contentText}>
            <p className={styles.label}>Upload content from .csv</p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className={styles.fileInput}
            />
          </div>
        </div>

        <button className={styles.btn} onClick={() => uploadContent()}>
          {selectedFile ? `Upload ${selectedFile.name}` : "Upload"}
        </button>
      </div>
    </div>
  );
};

export default MainContent;
